import React, { useEffect, useState } from 'react';
import { progressAPI } from '../../lib/api';
import { motion, AnimatePresence } from 'framer-motion';
import { BarChart3, CheckCircle2, XCircle, Flame, Award, ChevronDown, ChevronUp, Star, Clock, Edit3, Share2 } from 'lucide-react';
import { toast } from 'sonner';
import ShareReportCard from './ShareReportCard';

function getScoreColor(pct) {
  if (pct >= 90) return '#10B981';
  if (pct >= 70) return '#2563EB';
  if (pct >= 50) return '#FF9100';
  return '#EF4444';
}

function ConsistencyRing({ value }) {
  const r = 38;
  const circ = 2 * Math.PI * r;
  const offset = circ - (value / 100) * circ;
  return (
    <svg width="96" height="96" className="transform -rotate-90">
      <circle cx="48" cy="48" r={r} fill="none" stroke="#1C2B54" strokeWidth="6" />
      <circle
        cx="48" cy="48" r={r} fill="none"
        stroke={getScoreColor(value)}
        strokeWidth="6"
        strokeDasharray={circ}
        strokeDashoffset={offset}
        strokeLinecap="round"
        className="transition-all duration-1000"
      />
    </svg>
  );
}

function EffortOverride({ progressId, currentEffort, parentOverride, onOverride }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(parentOverride || currentEffort);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await progressAPI.overrideEffort(progressId, val);
      toast.success(`Effort updated to ${val}/10`);
      onOverride(val);
      setEditing(false);
    } catch {
      toast.error('Failed to update');
    }
    setSaving(false);
  };

  if (!editing) {
    return (
      <button
        onClick={() => setEditing(true)}
        className="flex items-center gap-1 text-[9px] text-[#64748B] hover:text-[#2563EB] transition-colors"
        data-testid={`override-btn-${progressId}`}
      >
        <Edit3 size={9} />
        {parentOverride ? `Parent: ${parentOverride}/10` : 'Override'}
      </button>
    );
  }

  return (
    <div className="flex items-center gap-2 mt-1">
      <input
        type="range" min={1} max={10} value={val}
        onChange={e => setVal(parseInt(e.target.value))}
        className="w-16 accent-[#2563EB] h-1.5"
        data-testid={`override-slider-${progressId}`}
      />
      <span className="text-[10px] font-bold text-[#2563EB] w-5">{val}</span>
      <button
        onClick={save} disabled={saving}
        className="text-[9px] font-bold text-[#10B981] hover:text-[#059669]"
        data-testid={`override-save-${progressId}`}
      >{saving ? '...' : 'Save'}</button>
      <button
        onClick={() => setEditing(false)}
        className="text-[9px] font-bold text-[#64748B] hover:text-[#0A2242]"
      >X</button>
    </div>
  );
}

export default function ParentDashboard() {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedDay, setExpandedDay] = useState(null);
  const [showShare, setShowShare] = useState(false);

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      const res = await progressAPI.parentReport();
      setReport(res.data);
    } catch { /* ignore */ }
    setLoading(false);
  };

  if (loading) {
    return <div className="flex justify-center py-12"><div className="spinner" /></div>;
  }

  if (!report) {
    return (
      <div className="py-8 text-center text-[#64748B] text-sm">No training data yet.</div>
    );
  }

  const { summary, days } = report;

  return (
    <div className="space-y-4" data-testid="parent-dashboard">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#2563EB]/10 to-[#10B981]/10 border border-[#2563EB]/20 rounded-xl p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-[9px] text-[#64748B] uppercase tracking-widest font-bold">Parent Progress Report</p>
            <h2 className="font-['Barlow_Condensed'] text-lg font-bold tracking-tight mt-1">
              Week {report.week_number} — {report.player_name}
            </h2>
          </div>
          <button
            onClick={() => setShowShare(true)}
            className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-[#2563EB]/15 text-[#2563EB] text-[10px] font-bold uppercase tracking-wider border border-[#2563EB]/30 hover:bg-[#2563EB]/25 transition-colors"
            data-testid="open-share-report-btn"
          >
            <Share2 size={12} /> Share
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 gap-3">
        {/* Weekly Score */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white border border-[#E2E8F0] rounded-xl p-4 text-center"
        >
          <p className="text-[8px] text-[#64748B] uppercase tracking-widest font-bold mb-1">Weekly Score</p>
          <p className="font-['Barlow_Condensed'] text-3xl font-black" style={{ color: getScoreColor(summary.weekly_score) }} data-testid="parent-weekly-score">
            {summary.weekly_score}<span className="text-sm text-[#64748B]">/100</span>
          </p>
        </motion.div>

        {/* Consistency */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05 }}
          className="bg-white border border-[#E2E8F0] rounded-xl p-4 flex flex-col items-center"
        >
          <p className="text-[8px] text-[#64748B] uppercase tracking-widest font-bold mb-1">Consistency</p>
          <div className="relative">
            <ConsistencyRing value={summary.consistency_score} />
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="font-['Barlow_Condensed'] text-xl font-black" style={{ color: getScoreColor(summary.consistency_score) }} data-testid="parent-consistency">
                {summary.consistency_score}%
              </span>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Stats Row */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="flex gap-2"
      >
        <div className="flex-1 bg-white border border-[#E2E8F0] rounded-xl p-3 text-center">
          <p className="text-[8px] text-[#64748B] uppercase tracking-widest">Days Trained</p>
          <p className="font-['Barlow_Condensed'] text-xl font-bold text-[#0A2242]" data-testid="parent-days-trained">
            {summary.days_trained}<span className="text-xs text-[#64748B]">/{summary.total_days}</span>
          </p>
        </div>
        <div className="flex-1 bg-white border border-[#E2E8F0] rounded-xl p-3 text-center">
          <p className="text-[8px] text-[#64748B] uppercase tracking-widest">Drills Done</p>
          <p className="font-['Barlow_Condensed'] text-xl font-bold text-[#0A2242]">
            {summary.total_drills_completed}<span className="text-xs text-[#64748B]">/{summary.total_drills}</span>
          </p>
        </div>
        <div className="flex-1 bg-white border border-[#E2E8F0] rounded-xl p-3 text-center">
          <p className="text-[8px] text-[#64748B] uppercase tracking-widest">Streak</p>
          <p className="font-['Barlow_Condensed'] text-xl font-bold text-[#FF9100] flex items-center justify-center gap-1">
            <Flame size={16} /> {summary.current_streak}
          </p>
        </div>
      </motion.div>

      {/* Badges */}
      {summary.badges?.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          className="bg-white border border-[#E2E8F0] rounded-xl p-4"
        >
          <p className="text-[8px] text-[#64748B] uppercase tracking-widest font-bold mb-2">Badges Earned</p>
          <div className="flex gap-3">
            {summary.badges.map(badge => (
              <div key={badge.name} className="flex flex-col items-center gap-1">
                <div className="w-10 h-10 rounded-full flex items-center justify-center border-2" style={{ borderColor: badge.color, background: `${badge.color}15` }}>
                  <Award size={20} style={{ color: badge.color }} />
                </div>
                <span className="text-[9px] font-bold" style={{ color: badge.color }}>{badge.name}</span>
              </div>
            ))}
          </div>
        </motion.div>
      )}

      {/* Daily Breakdown */}
      <div className="space-y-2">
        <p className="text-[9px] text-[#64748B] uppercase tracking-widest font-bold">Daily Breakdown</p>
        {days.map((day, di) => {
          const expanded = expandedDay === di;
          return (
            <motion.div
              key={di}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 + di * 0.04 }}
              className="bg-white border border-[#E2E8F0] rounded-xl overflow-hidden"
            >
              {/* Day Header */}
              <button
                onClick={() => setExpandedDay(expanded ? null : di)}
                className="w-full flex items-center gap-3 p-3 text-left"
                data-testid={`parent-day-${di}`}
              >
                <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `${getScoreColor(day.score_percentage)}15` }}>
                  <span className="text-xs font-bold" style={{ color: getScoreColor(day.score_percentage) }}>
                    {day.drills_completed > 0 ? day.score_percentage : '—'}
                  </span>
                </div>
                <div className="flex-1">
                  <p className="font-['Barlow_Condensed'] text-sm font-bold">{day.day_label}</p>
                  <p className="text-[9px] text-[#64748B]">
                    {day.drills_completed}/{day.drills_total} drills
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  {day.drills_completed === day.drills_total && day.drills_total > 0 ? (
                    <CheckCircle2 size={16} className="text-[#10B981]" />
                  ) : day.drills_completed > 0 ? (
                    <Clock size={16} className="text-[#FF9100]" />
                  ) : (
                    <XCircle size={16} className="text-[#64748B]" />
                  )}
                  {expanded ? <ChevronUp size={14} className="text-[#64748B]" /> : <ChevronDown size={14} className="text-[#64748B]" />}
                </div>
              </button>

              {/* Expanded Drill Details */}
              <AnimatePresence>
                {expanded && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    className="border-t border-[#E2E8F0]"
                  >
                    <div className="p-3 space-y-2">
                      {day.drills.map((drill, dri) => (
                        <div
                          key={dri}
                          className={`flex items-start gap-3 p-2.5 rounded-lg ${
                            drill.completed ? 'bg-[#F8FAFC]' : 'bg-[#F8FAFC]/50'
                          }`}
                          data-testid={`parent-drill-${di}-${dri}`}
                        >
                          {drill.completed ? (
                            <CheckCircle2 size={14} className="text-[#10B981] mt-0.5 flex-shrink-0" />
                          ) : (
                            <XCircle size={14} className="text-[#64748B] mt-0.5 flex-shrink-0" />
                          )}
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-bold truncate">{drill.title}</p>
                            <p className="text-[9px] text-[#64748B] uppercase">{drill.category}</p>
                            {drill.completed && (
                              <div className="mt-1.5 space-y-1">
                                <div className="flex items-center gap-3 text-[9px]">
                                  <span className="text-[#64748B]">Completion: <span className="text-[#0A2242] font-bold">{drill.score?.completion}/10</span></span>
                                  <span className="text-[#64748B]">Effort: <span className="text-[#0A2242] font-bold">{drill.score?.effort}/10</span></span>
                                  <span className="text-[#64748B]">Consistency: <span className="text-[#0A2242] font-bold">{drill.score?.consistency}/10</span></span>
                                </div>
                                <div className="flex items-center justify-between">
                                  <span className="text-[9px] font-bold" style={{ color: getScoreColor(Math.round(drill.score?.total / 30 * 100)) }}>
                                    Total: {drill.score?.total}/30
                                  </span>
                                  <EffortOverride
                                    progressId={drill.progress_id}
                                    currentEffort={drill.effort_rating}
                                    parentOverride={drill.parent_effort_override}
                                    onOverride={(newVal) => {
                                      // Refresh report
                                      load();
                                    }}
                                  />
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          );
        })}
      </div>

      {/* Share Report Overlay */}
      {showShare && (
        <ShareReportCard report={report} onClose={() => setShowShare(false)} />
      )}
    </div>
  );
}
